/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { apiDelete, apiGet, apiPatch, apiPost } from "./Api/apiCall";
import { MediaObject } from "../types/MediaObject";
import { Notification as BackofficeNotification } from "../types/Notification";
import { HydraCollectionResult } from "../types/HydraCollectionResult";
import {
  ReleaseUpdate,
  ReleaseUpdateMessageInput,
  ReleaseUpdatePublication,
} from "../types/ReleaseUpdate";

export const RELEASE_UPDATE_MAX_IMAGE_COUNT = 10;
export const RELEASE_UPDATE_MAX_IMAGE_SIZE = 15 * 1024 * 1024;
export const RELEASE_UPDATE_PUBLICATIONS_PER_PAGE = 10;
export const RELEASE_UPDATE_LOGIN_STORAGE_KEY = "showLatestReleaseUpdateAfterLogin";

export const setShowLatestReleaseUpdateAfterLoginFlag = () => {
  sessionStorage.setItem(RELEASE_UPDATE_LOGIN_STORAGE_KEY, "true");
};

export const consumeShowLatestReleaseUpdateAfterLoginFlag = () => {
  const shouldOpen = sessionStorage.getItem(RELEASE_UPDATE_LOGIN_STORAGE_KEY) === "true";
  sessionStorage.removeItem(RELEASE_UPDATE_LOGIN_STORAGE_KEY);
  return shouldOpen;
};

export const getReleaseUpdateArchivePath = (isExternal: boolean) => {
  return `/services/${isExternal ? "extern" : "tools"}/release-updates`;
};

export const getReleaseUpdateMediaObjectIds = (publications: ReleaseUpdatePublication[]) => {
  return Array.from(
    new Set(
      publications.flatMap((publication) => publication.messages.flatMap((message) => message.mediaObjectIds)),
    ),
  );
};

const fetchJsonCollection = <T>(iri: string, data: Record<string, string | boolean> = {}) => {
  const headers = new Headers();
  headers.set("Accept", "application/json");

  return apiGet<T[]>(iri, null, data, { headers });
};

export const fetchReleaseUpdateMediaObjects = async (mediaObjectIds: number[]): Promise<MediaObject[]> => {
  if (mediaObjectIds.length === 0) {
    return [];
  }

  const idFilterQuery = mediaObjectIds.map((mediaObjectId) => `id[]=${mediaObjectId}`).join("&");
  return fetchJsonCollection<MediaObject>(`media_objects?${idFilterQuery}`);
};

const getPublicationIri = (publicationId: number) => {
  return `/release_update_publications/${publicationId}`;
};

const updateReleaseUpdatePublicationLastModifiedBy = (publicationId: number, backofficeUserIri: string) => {
  return apiPatch<ReleaseUpdatePublication>("release_update_publications", publicationId, {
    lastModifiedBy: backofficeUserIri,
  });
};

const fetchReleaseUpdateCollection = (filters: Record<string, string | boolean> = {}) => {
  return fetchJsonCollection<ReleaseUpdatePublication>("release_update_publications", filters);
};

const fetchPaginatedReleaseUpdateCollection = (page: number, filters: Record<string, string | boolean>) => {
  return apiGet<HydraCollectionResult<ReleaseUpdatePublication>>(
    "release_update_publications",
    null,
    { ...filters, page },
  );
};

export const fetchOrCreateReleaseUpdateDraft = async (backofficeUserIri: string) => {
  const [draft] = await fetchReleaseUpdateCollection({ "exists[publishedAt]": false });
  if (draft) {
    return draft;
  }

  return apiPost<ReleaseUpdatePublication>("release_update_publications", {
    createdBy: backofficeUserIri,
    lastModifiedBy: backofficeUserIri,
  });
};

export const updateReleaseUpdatePublicationDraftVisibility = (
  publication: Pick<ReleaseUpdatePublication, "id">,
  visibleToInternal: boolean,
  visibleToExternal: boolean,
  backofficeUserIri: string,
) => {
  return apiPatch<ReleaseUpdatePublication>("release_update_publications", publication.id, {
    visibleToInternal,
    visibleToExternal,
    lastModifiedBy: backofficeUserIri,
  });
};

export const createReleaseUpdateMessage = async (
  publication: ReleaseUpdatePublication,
  message: ReleaseUpdateMessageInput,
  backofficeUserIri: string,
) => {
  const position = Math.max(0, ...publication.messages.map((existingMessage) => existingMessage.position)) + 1;
  const createdMessage = await apiPost<ReleaseUpdate>("release_updates", {
    publication: getPublicationIri(publication.id),
    position,
    ...message,
    createdBy: backofficeUserIri,
    lastModifiedBy: backofficeUserIri,
  });
  await updateReleaseUpdatePublicationLastModifiedBy(publication.id, backofficeUserIri);

  return createdMessage;
};

export const updateReleaseUpdateMessage = async (
  publicationId: number,
  existingMessage: Pick<ReleaseUpdate, "id" | "mediaObjectIds">,
  message: ReleaseUpdateMessageInput,
  backofficeUserIri: string,
) => {
  const updatedMessage = await apiPatch<ReleaseUpdate>("release_updates", existingMessage.id, {
    ...message,
    lastModifiedBy: backofficeUserIri,
  });
  await updateReleaseUpdatePublicationLastModifiedBy(publicationId, backofficeUserIri);
  await Promise.all(
    existingMessage.mediaObjectIds
      .filter((mediaObjectId) => !message.mediaObjectIds.includes(mediaObjectId))
      .map((mediaObjectId) => deleteReleaseUpdateMediaObject(mediaObjectId)),
  );

  return updatedMessage;
};

export const deleteReleaseUpdateMessage = async (
  publication: ReleaseUpdatePublication,
  message: Pick<ReleaseUpdate, "id">,
  backofficeUserIri: string,
) => {
  await apiDelete<null>("release_updates", message.id);
  const remainingMessages = publication.messages.filter((existingMessage) => existingMessage.id !== message.id);
  await Promise.all(
    remainingMessages.map((remainingMessage, index) => {
      const position = index + 1;
      if (remainingMessage.position === position) {
        return Promise.resolve();
      }
      return apiPatch<ReleaseUpdate>("release_updates", remainingMessage.id, {
        position,
        lastModifiedBy: backofficeUserIri,
      });
    }),
  );
  await updateReleaseUpdatePublicationLastModifiedBy(publication.id, backofficeUserIri);

  return fetchReleaseUpdatePublication(publication.id);
};

export const reorderReleaseUpdateMessages = async (
  publication: Pick<ReleaseUpdatePublication, "id">,
  releaseUpdateIds: number[],
  backofficeUserIri: string,
) => {
  await Promise.all(
    releaseUpdateIds.map((releaseUpdateId, index) => {
      return apiPatch<ReleaseUpdate>("release_updates", releaseUpdateId, {
        position: index + 1,
        lastModifiedBy: backofficeUserIri,
      });
    }),
  );
  await updateReleaseUpdatePublicationLastModifiedBy(publication.id, backofficeUserIri);

  return fetchReleaseUpdatePublication(publication.id);
};

export const publishReleaseUpdate = (
  publication: Pick<ReleaseUpdatePublication, "id">,
  backofficeUserIri: string,
) => {
  return apiPatch<ReleaseUpdatePublication>("release_update_publications", publication.id, {
    publishedAt: new Date().toISOString(),
    lastModifiedBy: backofficeUserIri,
  });
};

export const fetchReleaseUpdateManagementPublications = (page: number) => {
  return fetchPaginatedReleaseUpdateCollection(page, { "exists[publishedAt]": true });
};

export const fetchReleaseUpdateManagementPublication = (publicationId: number) => {
  return fetchReleaseUpdatePublication(publicationId);
};

export const fetchReleaseUpdatePublications = (isExternal: boolean, page: number) => {
  return fetchPaginatedReleaseUpdateCollection(page, {
    "exists[publishedAt]": true,
    "exists[messages]": true,
    [isExternal ? "visibleToExternal" : "visibleToInternal"]: true,
  });
};

export const fetchReleaseUpdatePublication = (publicationId: number) => {
  return apiGet<ReleaseUpdatePublication>("release_update_publications", publicationId);
};

export const fetchLatestUnreadReleaseUpdatePublication = async (backofficeUserIri: string) => {
  const notifications = await fetchJsonCollection<BackofficeNotification>("notifications", {
    backofficeUser: backofficeUserIri,
    type: "release-update",
    isRead: false,
  });
  const publicationId = notifications[0]?.releaseUpdatePublicationId;

  return publicationId ? fetchReleaseUpdatePublication(publicationId) : null;
};

export const markReleaseUpdatePublicationAsRead = async (
  publicationId: number,
  backofficeUserIri: string,
) => {
  const notifications = await fetchJsonCollection<BackofficeNotification>("notifications", {
    backofficeUser: backofficeUserIri,
    releaseUpdatePublication: getPublicationIri(publicationId),
    isRead: false,
  });
  const notification = notifications[0];
  if (notification) {
    await apiPatch<BackofficeNotification>("notifications", notification.id, { isRead: true });
  }
};

export const uploadReleaseUpdateImage = async (file: File): Promise<MediaObject> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("description", file.name);
  formData.append("fieldname", "releaseUpdate");

  return apiPost<MediaObject>("media_objects", formData);
};

export const deleteReleaseUpdateMediaObject = async (mediaObjectId: number): Promise<void> => {
  await apiDelete<null>("media_objects", mediaObjectId);
};
